import { z } from "zod";
import { doubleEncode, makeGetRequest } from "../../utils";
type GetSessionDetailsOptions = {
	/**The session's ID. If the ID begins with a / character or contains // characters, you must double-encode the ID value.

Examples: KkMHZ4y8QhSUWAHi5sWvfg== */
	sessionId: string;
	/**
	 * Default: live
Allowed: past ┃ live
The session types:

past — A past session.
live — A live session.
This value defaults to live.

Examples: past
	 */
	type?: "past" | "live";
};

export const getSessionDetailsResponseSchema = z
	.object({
		id: z.string(),
		session_name: z.string(),
		start_time: z.string(),
		end_time: z.string(),
		duration: z.string(),
		user_count: z.number(),
		has_voip: z.boolean(),
		has_video: z.boolean(),
		has_screen_share: z.boolean(),
		has_recording: z.boolean(),
		has_pstn: z.boolean(),
		session_key: z.string().nullish(),
	})
	.deepPartial();

type GetSessionDetailsResponse = z.infer<
	typeof getSessionDetailsResponseSchema
>;

/**Use this API to return information about live or past sessions. You can specify a monthly date range for this data using the from and to query parameters. The month should fall within the last six months.

The overview response displays whether features such as audio, video, screen sharing, and recording were used during the session. */
const getSessionDetails = async (
	token: string,
	options: GetSessionDetailsOptions,
): Promise<GetSessionDetailsResponse> => {
	const res = await makeGetRequest({
		token,
		pathList: [
			"v2",
			"videosdk",
			"sessions",
			// The session's ID. If the ID begins with a / character or contains // characters, you must double-encode the ID value.
			doubleEncode(options.sessionId),
		],
		options: { ...options, type: options.type ?? "live" },
	});
	const parsed = getSessionDetailsResponseSchema.parse(res);
	return parsed;
};

export const curriedGetSessionDetails = (token: string) => (
	options: GetSessionDetailsOptions,
) => getSessionDetails(token, options);